import { useTranslation } from 'react-i18next';

export type TrOptions = Record<string, string | number>;

/**
 * Fills `{{name}}` placeholders in the English fallback the way i18next fills
 * them in a real translation, so a key that has not landed yet still reads as a
 * sentence rather than showing raw braces.
 */
export function interpolate(text: string, options: TrOptions | undefined): string {
  if (!options) {
    return text;
  }
  return text.replace(/\{\{(\w+)\}\}/g, (match, name: string) =>
    options[name] === undefined ? match : String(options[name]),
  );
}

/**
 * `t()` with an English fallback for keys that have not reached the CDN
 * bundle yet. react-i18next returns the key itself when nothing is found, so
 * without this a fresh panel renders "editor-reflow-row-optional" instead of
 * "Optional" until the next translations publish.
 *
 * `options` is passed straight to i18next, so interpolation and `count`-based
 * plurals work as soon as the key exists; until then the fallback gets the same
 * values substituted in.
 */
function useTr() {
  const { t } = useTranslation();
  return (key: string, fallback: string, options?: TrOptions): string => translateOr(t, key, fallback, options);
}

// The same fallback outside React, for operations that get `i18n.t` as a thunk argument.
export function translateOr(
  t: (key: string, options?: TrOptions) => string,
  key: string,
  fallback: string,
  options?: TrOptions,
): string {
  const translated = t(key, options);
  return translated === key ? interpolate(fallback, options) : translated;
}

export default useTr;
